/**
 * PrivyAdapter - Convert Privy wallet objects to OwnYou interfaces
 *
 * Adapts @privy-io/react-auth ConnectedWallet to OwnYou's
 * Wallet and WalletSigner interfaces, keeping the identity
 * layer provider-agnostic.
 *
 * @see docs/architecture/OwnYou_architecture_v13.md Section 7
 */
export class PrivyAdapter {
    /**
     * Convert a Privy ConnectedWallet to an OwnYou Wallet.
     */
    toOwnYouWallet(privyWallet) {
        return {
            address: privyWallet.address,
            providerType: privyWallet.walletClientType === 'privy' ? 'privy' : 'external',
            chainId: privyWallet.chainId,
        };
    }
    /**
     * Create a WalletSigner from a Privy wallet for key derivation.
     * Uses the wallet's EIP-1193 provider for eth_signTypedData_v4.
     */
    createSigner(privyWallet) {
        const address = privyWallet.address;
        return {
            getAddress: () => address,
            signTypedData: async (params) => {
                const provider = await privyWallet.getEthereumProvider();
                // EIP-712 eth_signTypedData_v4 expects [address, typedDataJSON]
                const typedData = {
                    types: {
                        EIP712Domain: buildDomainType(params.domain),
                        ...params.types,
                    },
                    domain: params.domain,
                    primaryType: params.primaryType,
                    message: params.message,
                };
                const signature = await provider.request({
                    method: 'eth_signTypedData_v4',
                    params: [address, JSON.stringify(typedData)],
                });
                return signature;
            },
        };
    }
    /**
     * Get the embedded wallet from a list of connected wallets.
     * Returns null if no embedded wallet is found.
     */
    getEmbeddedWallet(wallets) {
        return wallets.find((w) => w.walletClientType === 'privy') ?? null;
    }
}
/** Build EIP712Domain type array from domain object */
function buildDomainType(domain) {
    const fields = [];
    if (domain.name !== undefined)
        fields.push({ name: 'name', type: 'string' });
    if (domain.version !== undefined)
        fields.push({ name: 'version', type: 'string' });
    if (domain.chainId !== undefined)
        fields.push({ name: 'chainId', type: 'uint256' });
    if (domain.verifyingContract !== undefined)
        fields.push({ name: 'verifyingContract', type: 'address' });
    if (domain.salt !== undefined)
        fields.push({ name: 'salt', type: 'bytes32' });
    return fields;
}
